import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { getLastNMonthsRanges } from '@qdrant-cloud-ui/date/get-last-n-months-ranges';
import { PaymentService } from '@qdrant/qdrant-cloud-public-api/payment/v1';
import { createFileRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { assertRpcPermissions } from '@/api/permissions';
import { BillingServices } from '@/api/services/billing';
import { meteringService } from '@/api/services/metering';
import { paymentService } from '@/api/services/payment';
import { usageBreakdownSearchSchema } from '@/components/Billing/UsageBreakdown/searchParams';
import { checkMultiAccountBillingPageFeatureGate } from '@/helpers/statsig/featureGate';
import { ensureStatsigUserFeatureGateReady } from '@/helpers/statsig/featureGate/ensureStatsigUserFeatureGateReady';
import { head } from '@/router/Head';
import { resolveAccountHasPrivileges } from '@/router/resolvers/user';
import { resolveBillingParentAccountId } from './billing.resolvers';

const DEFAULT_USAGE_MONTHS = 3;
const HIDE_BILLING_PAGE = 'HIDE_BILLING_PAGE';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/billing')({
  validateSearch: usageBreakdownSearchSchema,
  staticData: {
    title: 'Billing',
    // Overrides the inherited `account` default — billing/usage errors belong to the billing area.
    domain: 'billing',
  },
  beforeLoad: async ({ params: { accountId }, context }) => {
    await ensureStatsigUserFeatureGateReady(context);
    if (await resolveAccountHasPrivileges([HIDE_BILLING_PAGE], { accountId, ...context })) {
      throw notFound();
    }

    // The gate, read from the permissions `$accountId` already awaited: an AccessControlError — rendered
    // as "Access Denied" — when the account may not read payment information. Checked here rather than
    // by awaiting the payment methods, so the page opens at once and each section loads on its own.
    assertRpcPermissions([PaymentService.method.listPaymentMethods], { queryClient: context.queryClient, accountId });

    const { statsigClient } = context;
    const isMultiAccountBillingPageEnabled = checkMultiAccountBillingPageFeatureGate(statsigClient);

    let getBillingAccountParentQuery;
    let isParentAccount = false;
    if (isMultiAccountBillingPageEnabled) {
      getBillingAccountParentQuery = BillingServices.getBillingAccountParentQuery({ accountId });
      const parentAccountId = await resolveBillingParentAccountId({ ...context, getBillingAccountParentQuery });
      isParentAccount = parentAccountId === accountId;
    }

    return {
      listPaymentMethodsQuery: paymentService.listPaymentMethodsQuery({ accountId }),
      listInvoicesQuery: BillingServices.listInvoicesQuery({ accountId }),
      listMonthlyMeteringsQuery: meteringService.listMonthlyMeteringsQuery({ accountId }),
      listCreditContractsQuery: isParentAccount ? BillingServices.listCreditContractsQuery({ accountId }) : undefined,
      listBillingAccountChildrenQuery: isParentAccount
        ? BillingServices.listBillingAccountChildrenQuery({ accountId })
        : undefined,
      // One query per month, matching the per-month fetch in UsageBreakdown so the prefetch warms
      // the same keys (rather than firing a single wide, slow range the component never reads).
      getUsageBreakdownQueries: getLastNMonthsRanges(DEFAULT_USAGE_MONTHS).map(({ start, end }) =>
        meteringService.getUsageBreakdownQuery({
          accountId,
          startTime: timestampFromDate(start),
          endTime: timestampFromDate(end),
        }),
      ),
      getBillingAccountParentQuery,
    };
  },
  // Not awaited: every section reads its data behind its own Suspense boundary and skeleton.
  loader: ({
    context: {
      queryClient,
      listPaymentMethodsQuery,
      listInvoicesQuery,
      listMonthlyMeteringsQuery,
      listCreditContractsQuery,
      listBillingAccountChildrenQuery,
      getUsageBreakdownQueries,
      getBillingAccountParentQuery,
    },
  }) => {
    void queryClient.prefetchQuery(listPaymentMethodsQuery);
    void queryClient.prefetchQuery(listInvoicesQuery);
    void queryClient.prefetchQuery(listMonthlyMeteringsQuery);
    if (listCreditContractsQuery) {
      void queryClient.prefetchQuery(listCreditContractsQuery);
    }
    if (listBillingAccountChildrenQuery) {
      void queryClient.prefetchQuery(listBillingAccountChildrenQuery);
    }
    if (getUsageBreakdownQueries) {
      for (const query of getUsageBreakdownQueries) {
        void queryClient.prefetchQuery(query);
      }
    }
    if (getBillingAccountParentQuery) {
      void queryClient.prefetchQuery(getBillingAccountParentQuery);
    }
  },
  head,
  pendingMs: 200,
  component: lazyRouteComponent(() => import('./billing.component'), 'BillingComponent'),
  pendingComponent: lazyRouteComponent(() => import('./billing.component'), 'BillingPendingComponent'),
});
